import type { ReactNode } from "react";
import { Badge, Icon, type BadgeTone } from "@/components/ui";
import type {
  Template,
  TemplateCategory,
  TemplateClause,
  TemplateScope,
  TemplateStatus,
  TemplateVariable,
} from "@/types/template";

const CATEGORY_LABELS: Record<string, string> = {
  CONTRACT: "Contrato",
  NDA: "NDA",
  POWER_OF_ATTORNEY: "Poder",
  POLICY: "Política",
  OTHER: "Otro",
};

const RISK_LABELS: Record<string, string> = {
  LOW: "Bajo",
  MEDIUM: "Medio",
  HIGH: "Alto",
  CRITICAL: "Crítico",
};

const VAR_TYPE_LABELS: Record<string, string> = {
  STRING: "Texto",
  NUMBER: "Número",
  DATE: "Fecha",
  BOOLEAN: "Sí / No",
  ENUM: "Lista de opciones",
};

export function varTypeLabel(type: string | null | undefined): string {
  return type ? (VAR_TYPE_LABELS[type] ?? type) : "—";
}

export function categoryLabel(category: string | undefined): string {
  return category ? (CATEGORY_LABELS[category] ?? category) : "Sin categoría";
}

export function riskLabel(risk: string | null | undefined): string {
  return risk ? (RISK_LABELS[risk] ?? risk) : "Sin riesgo";
}

export function relativeDate(value: string | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60_000);
  if (diffMinutes < 1) return "ahora";
  if (diffMinutes < 60) return `hace ${diffMinutes} min`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `hace ${diffHours} h`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return "ayer";
  if (diffDays < 30) return `hace ${diffDays} días`;
  return date.toLocaleDateString("es-PE");
}

export function TemplateScopeBadge({
  scope,
}: {
  scope: TemplateScope | undefined;
}) {
  const global = scope === "GLOBAL";
  return (
    <Badge
      tone={global ? "info" : "outline"}
      size="sm"
      icon={global ? "globe" : "building"}
    >
      {global ? "Del sistema" : "De mi estudio"}
    </Badge>
  );
}

const STATUS_BADGES: Record<
  TemplateStatus,
  { tone: BadgeTone; label: string }
> = {
  DRAFT: { tone: "neutral", label: "Borrador" },
  IN_REVIEW: { tone: "warn", label: "En revisión" },
  PUBLISHED: { tone: "success", label: "Publicada" },
  REJECTED: { tone: "danger", label: "Rechazada" },
  ARCHIVED: { tone: "neutral", label: "Archivada" },
};

export function TemplateStatusBadge({
  status,
}: {
  status: TemplateStatus | undefined;
}) {
  const cfg = STATUS_BADGES[status ?? "DRAFT"];
  return (
    <Badge tone={cfg.tone} size="sm">
      {cfg.label}
    </Badge>
  );
}

export function versionStatusLabel(status: string | undefined): string {
  if (status === "PUBLISHED") return "Publicada";
  if (status === "DRAFT") return "Borrador";
  if (status === "SUPERSEDED") return "Superada";
  return status ?? "—";
}

export function versionStatusTone(status: string | undefined) {
  if (status === "PUBLISHED") return "success" as const;
  if (status === "DRAFT") return "warn" as const;
  return "soft" as const;
}

export function TemplateCategoryBadge({
  category,
}: {
  category: string | undefined;
}) {
  return (
    <Badge tone="soft" size="sm">
      {categoryLabel(category)}
    </Badge>
  );
}

export function RiskBadge({
  riskLevel,
}: {
  riskLevel: TemplateClause["riskLevel"];
}) {
  if (!riskLevel)
    return (
      <Badge tone="soft" size="sm">
        Sin riesgo
      </Badge>
    );
  const tone =
    riskLevel === "CRITICAL" || riskLevel === "HIGH"
      ? "danger"
      : riskLevel === "MEDIUM"
        ? "warn"
        : "success";
  return (
    <Badge tone={tone} size="sm">
      {riskLabel(riskLevel)}
    </Badge>
  );
}

export function TemplateLockHint({
  template,
}: {
  template: Template;
}): ReactNode {
  if (template.scope !== "GLOBAL") return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-muted"
      title="Plantilla del sistema: solo el administrador de la plataforma puede editarla"
    >
      <Icon name="lock" size={12} />
      Sistema
    </span>
  );
}

export function templateCanMutate(
  template: Template,
  isSuperadmin: boolean,
): boolean {
  if (template.scope === "GLOBAL") return isSuperadmin;
  return true;
}

export function variableDisplayDefault(variable: TemplateVariable): string {
  if (
    variable.defaultValue === undefined ||
    variable.defaultValue === null ||
    variable.defaultValue === ""
  )
    return "—";
  if (typeof variable.defaultValue === "string") return variable.defaultValue;
  return JSON.stringify(variable.defaultValue);
}

export function normalizeCategory(value: string | undefined): TemplateCategory {
  const allowed: TemplateCategory[] = [
    "CONTRACT",
    "NDA",
    "POWER_OF_ATTORNEY",
    "POLICY",
    "OTHER",
  ];
  return allowed.includes(value as TemplateCategory)
    ? (value as TemplateCategory)
    : "CONTRACT";
}
